/**
 * E4. Блэкджек: карты — текстом в моно, линейками, без иллюстраций.
 * Ставка выбирается до первого «ещё» и затем закрепляется. «Ещё»
 * продвигает руку через `blackjackHit` в состоянии экрана; перебор
 * закрывает руку сразу, «Хватит» — тоже: оба вызывают `def.play(stand)`
 * с продвинутым состоянием (второй аргумент `onInput`). После
 * BLACKJACK_LEARN_HANDS рук под картами — подсказка базовой стратегии.
 */
import { t } from '@life/i18n';
import {
  BLACKJACK_DEALER_STANDS,
  BLACKJACK_LEARN_HANDS,
  BLACKJACK_NATURAL_PAYOUT,
  blackjackBasicAction,
  blackjackHit,
  handValue,
  isBust,
  type BlackjackInput,
  type BlackjackState,
  type Card,
} from '@life/minigames';
import { useState } from 'preact/hooks';
import { money } from '../lib/format';
import type { MinigameScreenProps } from './types';
import { Bet, betCap, useBet } from './ui/Bet';

function Hand({ label, cards, hidden }: { label: string; cards: readonly Card[]; hidden?: boolean }) {
  const shown = hidden ? cards.slice(0, 1) : cards;
  return (
    <div class="hand">
      <span class="hand-label">{label}</span>
      <span class="cards mono" aria-label={shown.join(' ')}>
        {shown.map((c, i) => (
          <span key={i} class="card">
            {c}
          </span>
        ))}
        {hidden && <span class="card back">{t('ui.minigame.blackjack.hidden')}</span>}
      </span>
      <span class="mono hand-value">{t('ui.minigame.blackjack.value', { n: handValue(shown) })}</span>
    </div>
  );
}

export function Blackjack({ state, life, onInput }: MinigameScreenProps<BlackjackInput, BlackjackState>) {
  const cap = betCap(state.maxBet, life.money);
  const [bet, setBet] = useBet(cap);
  const [local, setLocal] = useState<BlackjackState | null>(null);
  const current = local ?? state;
  const started = local !== null;
  const canAct = bet > 0;
  const dealerUp = state.dealer[0] as Card;
  const learned = state.hands >= BLACKJACK_LEARN_HANDS;

  const stand = (s: BlackjackState) => onInput({ bet, action: 'stand' }, s);
  const hit = () => {
    if (!canAct) return;
    const next = blackjackHit(current);
    if (isBust(next.hand) || handValue(next.hand) === 21) {
      stand(next);
      return;
    }
    setLocal(next);
  };

  return (
    <>
      <p class="hint">{t('ui.minigame.blackjack.rules', { stands: BLACKJACK_DEALER_STANDS, natural: BLACKJACK_NATURAL_PAYOUT })}</p>
      <Hand label={t('ui.minigame.blackjack.dealer')} cards={state.dealer} hidden />
      <Hand label={t('ui.minigame.blackjack.hand')} cards={current.hand} />
      {learned ? (
        <p class="hint bj-hint">
          {t('ui.minigame.blackjack.hint', {
            n: BLACKJACK_LEARN_HANDS,
            action: t(blackjackBasicAction(current.hand, dealerUp) === 'hit' ? 'ui.minigame.blackjack.hit' : 'ui.minigame.blackjack.stand'),
          })}
        </p>
      ) : (
        <p class="hint">{t('ui.minigame.blackjack.learning', { hands: state.hands, n: BLACKJACK_LEARN_HANDS })}</p>
      )}
      {started ? (
        <p class="hint">{t('ui.minigame.blackjack.betLocked', { sum: money(bet) })}</p>
      ) : (
        <Bet cap={cap} value={bet} onChange={setBet} />
      )}
      <div class="picks">
        <button type="button" class="btn" disabled={!canAct} onClick={hit}>
          {t('ui.minigame.blackjack.hit')}
        </button>
        <button type="button" class="btn accent" disabled={!canAct} onClick={() => stand(current)}>
          {t('ui.minigame.blackjack.stand')}
        </button>
      </div>
      {cap <= 0 && <p class="hint">{t('ui.minigame.bet.none')}</p>}
    </>
  );
}
